import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import * as Yup from 'yup';
import getTrackingParams from '../../../utils/getTrackingParams';
import { legacyParseFirstJsonInResponse } from '../../../utils/common/legacyCompatibility';
// eslint-disable-next-line import/no-extraneous-dependencies, import/order
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { yearsEmployed } from '../../../constants/formFields/yearsEmployed';
// import ExitIntentPopup from './ExitIntentPopup';
// import useExitIntent from './useExitIntent';

const PUBLIC_EMAIL_DOMAIN_TOKENS = [
  '126', '139', '163', 'alice', 'aol', 'asia', 'att', 'bell', 'bellsouth',
  'bigpond', 'bk', 'bluewin', 'blueyonder', 'bol', 'btinternet', 'charter',
  'comcast', 'cox', 'daum', 'earthlink', 'email', 'europe', 'fastmail',
  'foxmail', 'free', 'freenet', 'globo', 'gmail', 'gmx', 'googlemail',
  'hanmail', 'hispeed', 'hotmail', 'icloud', 'ig', 'iinet', 'iname', 'inbox',
  'interia', 'kpnmail', 'laposte', 'libero', 'list', 'live', 'mac', 'mail',
  'me', 'msn', 'naver', 'ntlworld', 'o2', 'onet', 'optusnet', 'orange',
  'outlook', 'planet', 'pm', 'post', 'posteo', 'proton', 'protonmail', 'qq',
  'rambler', 'rediffmail', 'rocketmail', 'rogers', 'sbcglobal', 'seznam',
  'sfr', 'sina', 'skynet', 'sky', 'sohu', 'sunrise', 'sympatico', 'talktalk',
  'telenet', 'terra', 'tiscali', 't-online', 'tuta', 'tutanota', 'ukr', 'uol',
  'usa', 'verizon', 'virginmedia', 'virgilio', 'wanadoo', 'web', 'wp',
  'xs4all', 'ya', 'yahoo', 'yandex', 'yeah', 'ymail', 'ziggo', 'zoho',
  'zohomail',
];

const isLikelyWorkEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const at = email.lastIndexOf('@');
  if (at < 1 || at === email.length - 1) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  // Wait until the user has typed a full domain: at least one dot followed by
  // a 2+ char TLD, so we don't flash on partial input like "name@xyz."
  const lastDot = domain.lastIndexOf('.');
  if (lastDot < 1 || domain.length - lastDot - 1 < 2) return false;
  if (PUBLIC_EMAIL_DOMAIN_TOKENS.some((token) => domain.includes(token))) return false;
  return true;
};

const isLikelyOnlyOneName = (name) => {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  if (!trimmed) return false;
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  return tokens.length < 2;
};

const FullNameHint = ({ name }) => {
  const [debouncedName, setDebouncedName] = useState(name);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(name), 700);
    return () => clearTimeout(timer);
  }, [name]);
  if (!isLikelyOnlyOneName(debouncedName)) return null;
  return (
    <i className="cdf__field__hint">
      Please enter your full name (first and last).
    </i>
  );
};

FullNameHint.propTypes = {
  name: PropTypes.string,
};

const validationSchema = Yup.object({
  'call-taken-by': Yup.string(),
  name: Yup.string().required('Required'),
  'job-title': Yup.string(),
  email: Yup.string().email('Invalid email').required('Required'),
  phone: Yup.number(),
  'years-employed': Yup.string().required('Required'),
  'still-employed': Yup.string().required('Required'),
  salary: Yup.string().required('Required'),
  'settlement-agreement': Yup.string(),
  'what-is-your-objective': Yup.string(),
  description: Yup.string()
    .min(200, 'Description must be at least 200 characters long')
    .required('Required'),
  terms: Yup.array().min(1, 'Required').required('Required'),
});

const CDF = ({ classNames = '', sideBar, contactFieldLayout = false }) => {
  const router = useRouter();
  const recognitionRef = useRef(null);
  const formikRef = useRef(null);
  const isUserLoggedIn = router.query.loggedIn === 'true';
  const [showToolTipYe, setShowToolTipYe] = useState(false);
  const [showToolTipAi, setShowToolTipAi] = useState(false);
  const [showToolTipSa, setShowToolTipSa] = useState(false);
  const [showToolTipDes, setShowToolTipDes] = useState(false);
  const [formSubmittedSuccessfully, setFormSubmittedSuccessfully] = useState(null);
  const [error, setError] = useState(null);
  const [formExpand, setFormExpand] = useState(true);
  const [isListening, setIsListening] = useState(false);
  // const { shouldShow, closePopup } = useExitIntent(1 * 60 * 1000); // 1 minute for testing

  const handleGAEventOnSubmit = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'form',
      form: {
        action: 'submit',
        id: 'cdf-react',
        name: 'CDF',
        type: 'Contact detailed form',
        category: 'Contact forms',
        success: true,
      },
    });
  };

  const trackingDataForMArketing = (values) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cdf1_details',
      years_employed: values['years-employed'],
      still_employed: values['still-employed'],
      annual_income: values['salary'],
      settlement: values['settlement-agreement'],
      leave_job: values['what-is-your-objective'],
    });
  };

  const affiliateMarketingOnSubmit = (dealID) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      TDUniqueID: dealID,
      ordervalue: 1,
    });
  };

  useEffect(() => {
    if (sideBar) {
      setFormExpand(false);
    }
    const test = getTrackingParams();
  }, [setFormExpand, sideBar]);

  const expand = () => {
    setFormExpand(true);
  };

  const preventDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const preventDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'none';
  };

  const prevTranscriptRef = useRef('');
  const [commandInput, setCommandInput] = useState('');
  const [manuallyStopped, setManuallyStopped] = useState(false);
  const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } =
    useSpeechRecognition();
  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      alert('Your browser does not support speech recognition. Try using Chrome or Edge.');
    }
  }, [browserSupportsSpeechRecognition]);

  // ✅ Update Formik field whenever transcript changes
  useEffect(() => {
    setCommandInput(transcript);
    if (formikRef.current) {
      // Update Formik field value with voice transcript
      formikRef.current.setFieldValue('description', transcript, true);
      // Mark field as touched to trigger validation and display errors
      formikRef.current.setFieldTouched('description', true, true);
    }
  }, [transcript]);

  const newFunctionForSpeech = () => {
    if (listening) {
      setManuallyStopped(true);
      SpeechRecognition.stopListening();
      prevTranscriptRef.current = '';
    } else {
      setManuallyStopped(false);
      SpeechRecognition.startListening({
        continuous: true, // Keeps listening even after pauses
        language: 'en-US', // You can change this to "hi-IN", "en-GB", etc.
      });
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      if (!isUserLoggedIn) {
        values.tracking = getTrackingParams();
      }
      const formType = 'CDF';
      const contact = true;
      values = { ...values, contact, formType, voice_used: !!transcript && !!values.description };

      const res = await axios
        .post(`${process.env.NEXT_PUBLIC_ROTA_URL}create-deal`, values)
        .then(legacyParseFirstJsonInResponse);
      const dealID = res?.data?.id;
      const deal_access_token = res?.data?.deal_access_token;
      // The AI summary is generated by create-deal itself. Calling it from here never worked
      // reliably: the redirect below cancels the request long before the endpoint returns.
      handleGAEventOnSubmit();
      affiliateMarketingOnSubmit(dealID);
      trackingDataForMArketing(values);
      setFormSubmittedSuccessfully(true);
      window.location.href = `/contact-us2?dealId=${dealID}&deal_access_token=${deal_access_token}&email=${encodeURIComponent(
        values.email,
      )}`;
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormSubmittedSuccessfully(false);
      if (error.response?.status === 409) {
        const dupMsg =
          error.response?.data?.error?.details ||
          error.response?.data?.details ||
          "You already have an open enquiry with us under this email or phone, so we haven't created a duplicate. Please check your email (including spam) for our reply, or contact communications@monacosolicitors.co.uk.";
        setError({ kind: 'duplicate-enquiry', message: dupMsg });
      } else {
        setError(error);
        Sentry.captureException(error, {
          tags: { page: 'CDF' },
          extra: {
            description: 'Error submitting CDF',
            context: { ...values },
          },
        });
      }
    }
  };

  const onClickYearsEmployed = () => {
    setShowToolTipYe(!showToolTipYe);
    setShowToolTipAi(false);
    setShowToolTipSa(false);
    setShowToolTipDes(false);
  };

  const onClickAnnualIncome = () => {
    setShowToolTipYe(false);
    setShowToolTipAi(!showToolTipAi);
    setShowToolTipSa(false);
    setShowToolTipDes(false);
  };

  const onClickSettlementAgreement = () => {
    setShowToolTipYe(false);
    setShowToolTipAi(false);
    setShowToolTipSa(!showToolTipSa);
    setShowToolTipDes(false);
  };

  const onClickDescription = () => {
    setShowToolTipYe(false);
    setShowToolTipAi(false);
    setShowToolTipSa(false);
    setShowToolTipDes(!showToolTipDes);
  };

  return (
    <>
      {/* <div className="whatsappIcon3">
        <img
          src="/images/WhatsAppDoIHaveCase.png"
          alt="whatsapp"
          className="whatsappIcon2"
          onClick={() => {
            const phoneNumber = '447344282544';
            const message = 'Hi, do I have a case?';
            const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
            window.open(url, '_blank');
          }}
        />
      </div> */}
      <div className={`cdf ${classNames}`} onClick={expand}>
        {/* <ExitIntentPopup show={shouldShow} onClose={closePopup} /> */}
        <Formik
          innerRef={formikRef}
          initialValues={{
            'call-taken-by': '',
            name: '',
            job: '',
            email: '',
            phone: '',
            'years-employed': '',
            'still-employed': '',
            salary: '',
            'settlement-agreement': '',
            'what-is-your-objective': '',
            description: '',
            terms: '',
          }}
          onSubmit={handleFormSubmit}
          validationSchema={validationSchema}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({ errors, touched, isValid, isSubmitting, setFieldValue, values }) => (
            <Form className="cdf__form">
              {isUserLoggedIn && (
                <div className="cdf__form__call-taken-by">
                  <p>Call taken by:</p>
                  <Field as="select" name="call-taken-by">
                    <option defaultValue="selected">-Select -</option>
                    <option value="Angelo">Angelo</option>
                    <option value="Annie">Annie</option>
                    <option value="Fiammetta">Fiammetta</option>
                    <option value="Haydn">Haydn</option>
                    <option value="Imogen">Imogen</option>
                    <option value="Sana">Sana</option>
                    <option value="Terenia">Terenia</option>
                    <option value="Tolu">Tolu</option>
                  </Field>
                </div>
              )}
              <div className="cdf__form__first-step">
                <h5>{sideBar ? 'Contact us today' : 'Your Details'}</h5>
                {sideBar && <p>We'll let you know how we can help within 1 working hour</p>}
                <div className="cdf__first-step__fields">
                  <div className="cdf__field__wrapper">
                    <p>
                      Full Name <span>*</span>
                    </p>
                    <Field type="text" name="name" onDragOver={preventDrag} onDrop={preventDrop} />
                    {errors.name && touched.name ? (
                      <i className="cdf__field__error">{errors.name}</i>
                    ) : null}
                    <FullNameHint name={values.name} />
                  </div>
                  {!contactFieldLayout && (
                    <div className="cdf__field__wrapper">
                      <p>Latest job title</p>
                      <Field type="text" name="job" onDragOver={preventDrag} onDrop={preventDrop} />
                    </div>
                  )}
                  <div className="cdf__field__wrapper">
                    <p>
                      Email <span>*</span>
                    </p>
                    <Field
                      type="email"
                      name="email"
                      onDragOver={preventDrag}
                      onDrop={preventDrop}
                    />
                    {errors.email && touched.email ? (
                      <i className="cdf__field__error">{errors.email}</i>
                    ) : null}
                    {isLikelyWorkEmail(values.email) && (
                      <i className="cdf__field__hint">
                        For your privacy, we recommend using a personal email, as work emails may be accessible by your employer.
                      </i>
                    )}
                  </div>

                  <div className="cdf__field__wrapper">
                    <p>Phone</p>
                    <Field
                      type="phone"
                      name="phone"
                      pattern="[0-9]*"
                      inputMode="numeric"
                      onDragOver={preventDrag}
                      onDrop={preventDrop}
                    />
                  </div>
                  {contactFieldLayout && (
                    <div className="cdf__field__wrapper text-area-speech">
                      <p>
                        Brief description of case<span>*</span>
                      </p>
                      <Field rows={12} name="description" component="textarea" maxLength="5000" />
                      <button
                        type="button"
                        onClick={() => newFunctionForSpeech()} // Pass the current description value
                      >
                        {isListening ? (
                          <img
                            src="/images/microphone.png"
                            width="24"
                            height="24"
                            alt="microphone"
                          />
                        ) : (
                          <img src="/images/mic.png" width="24" height="24" alt="microphone" />
                        )}
                      </button>
                      {errors.description && touched.description ? (
                        <i className="cdf__field__error">{errors.description}</i>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              {formExpand && (
                <div className="cdf__form__second-step">
                  <h5>Your Situation</h5>
                  <div className="cdf__second-step__fields">
                    {contactFieldLayout && (
                      <div className="cdf__field__wrapper">
                        <p>Latest job title</p>
                        <Field type="text" name="job" />
                      </div>
                    )}
                    <div className="cdf__second-step__select">
                      <div className="cdf__select-field__wrapper">
                        <div className="cdf__select-field__wrapper__inner">
                          <p>
                            Years employed <span>*</span>
                          </p>
                          <div>
                            <a href="JavaScript:void(0);" onClick={onClickYearsEmployed}>
                              <img
                                src="/images/info-icon.svg"
                                width="20"
                                height="20"
                                alt="info-icon"
                              />
                            </a>
                            {showToolTipYe && (
                              <div className="cdf__tooltip" onClick={() => setShowToolTipYe(false)}>
                                <div className="cdf__tooltip__inner">
                                  <p className="cdf__tooltip__inner__title">Years employed</p>
                                  <p className="cdf__tooltip__inner__paragraph">
                                    Average years in employment
                                  </p>
                                  <p className="cdf__tooltip__inner__action">Got it</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <Field as="select" name="years-employed">
                          <option value="" defaultValue="selected">
                            - Select -
                          </option>
                          {yearsEmployed.map(item => (
                            <option key={item.value} value={item.label}>{item.label}</option>
                          ))}
                        </Field>
                        {errors['years-employed'] && touched['years-employed'] ? (
                          <i className="cdf__field__error">{errors['years-employed']}</i>
                        ) : null}
                      </div>
                      <div className="cdf__select-field__wrapper">
                        <div className="cdf__select-field__wrapper__inner">
                          <p>
                            Still employed <span>*</span>
                          </p>
                        </div>
                        <Field as="select" name="still-employed">
                          <option value="" defaultValue="selected">
                            - Select -
                          </option>
                          <option value="Yes">Still employed</option>
                          <option value="Resigned">Resigned</option>
                          <option value="Dismissed">Dismissed</option>
                        </Field>
                        {errors['still-employed'] && touched['still-employed'] ? (
                          <i className="cdf__field__error">{errors['still-employed']}</i>
                        ) : null}
                      </div>

                      <div className="cdf__select-field__wrapper">
                        <div className="cdf__select-field__wrapper__inner">
                          <p>
                            Annual income <span>*</span>
                          </p>
                          <div>
                            <a href="JavaScript:void(0);" onClick={onClickAnnualIncome}>
                              <img
                                src="/images/info-icon.svg"
                                width="20"
                                height="20"
                                alt="info-icon"
                              />
                            </a>
                            {showToolTipAi && (
                              <div className="cdf__tooltip" onClick={() => setShowToolTipAi(false)}>
                                <div className="cdf__tooltip__inner">
                                  <p className="cdf__tooltip__inner__title">Salary</p>
                                  <p className="cdf__tooltip__inner__paragraph">
                                    We use this info to advise you on case value
                                  </p>
                                  <p className="cdf__tooltip__inner__action">Got it</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <Field as="select" name="salary">
                          <option value="" defaultValue="selected">
                            - Select -
                          </option>
                          <option value="£0 - £30,000">£0 - £30,000</option>
                          <option value="£30,000 - £50,000">£30,000 - £50,000</option>
                          <option value="£50,000 - £100,000">£50,000 - £100,000</option>
                          <option value="£100,000 +">£100,000 +</option>
                        </Field>
                        {errors.salary && touched.salary ? (
                          <i className="cdf__field__error">{errors.salary}</i>
                        ) : null}
                      </div>

                      <div className="cdf__select-field__wrapper">
                        <div className="cdf__select-field__wrapper__inner">
                          <p>Has your employer offered you money to leave?</p>
                          <div>
                            <a href="JavaScript:void(0);" onClick={onClickSettlementAgreement}>
                              <img
                                src="/images/info-icon.svg"
                                width="20"
                                height="20"
                                alt="info-icon"
                              />
                            </a>
                            {showToolTipSa && (
                              <div className="cdf__tooltip" onClick={() => setShowToolTipSa(false)}>
                                <div className="cdf__tooltip__inner">
                                  <p className="cdf__tooltip__inner__title">Settlement agreement</p>
                                  <p className="cdf__tooltip__inner__paragraph">
                                    Have you received a settlement offer
                                  </p>
                                  <p className="cdf__tooltip__inner__action">Got it</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <Field as="select" name="settlement-agreement">
                          <option value="" defaultValue="selected">
                            - Select -
                          </option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="Not sure">Not sure</option>
                        </Field>
                      </div>
                      <div className="cdf__select-field__wrapper">
                        <p>Want to leave job?</p>
                        <Field as="select" name="what-is-your-objective">
                          <option value="" defaultValue="selected">
                            - Select -
                          </option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="Maybe">Maybe</option>
                          <option value="Already left">Already left</option>
                        </Field>
                      </div>
                    </div>
                    {!contactFieldLayout && (
                      <div className="cdf__form__third-step">
                        <div className="cdf__form__third-step__inner">
                          <p>
                            Brief description of case <span>*</span>
                          </p>
                          <div>
                            <a href="JavaScript:void(0);" onClick={onClickDescription}>
                              <img
                                src="/images/info-icon.svg"
                                width="20"
                                height="20"
                                alt="info-icon"
                              />
                            </a>
                            {showToolTipDes && (
                              <div
                                className="cdf__tooltip"
                                onClick={() => setShowToolTipDes(false)}
                              >
                                <div className="cdf__tooltip__inner">
                                  <p className="cdf__tooltip__inner__title">Keep it brief</p>
                                  <p className="cdf__tooltip__inner__paragraph">
                                    Outline the key points, less is more
                                  </p>
                                  <p className="cdf__tooltip__inner__action">Got it</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-area-speech">
                          <Field name="description">
                            {({ field }) => (
                              <textarea
                                {...field}
                                rows={12}
                                onDragOver={preventDrag}
                                onDrop={preventDrop}
                                maxLength={5000}
                                value={commandInput} // your local state
                                onKeyUp={(e) => {
                                  setCommandInput(e.target.value); // update local state
                                }}
                                onChange={(e) => {
                                  field.onChange(e); // update Formik state
                                  setCommandInput(e.target.value); // update local state
                                }}
                              />
                            )}
                          </Field>
                          <button
                            type="button"
                            className=""
                            onClick={() => newFunctionForSpeech()} // Pass the current description value
                          >
                            {listening ? (
                              <img
                                src="/images/mic_blue.png"
                                width="24"
                                height="24"
                                className=""
                                alt="microphone"
                              />
                            ) : (
                              <>
                                <img
                                  src="/images/mic_cross_blue.png"
                                  className="blueColor"
                                  width="24"
                                  height="24"
                                  alt="microphone"
                                />
                              </>
                            )}
                          </button>
                        </div>
                        {errors.description && touched.description ? (
                          <i className="cdf__field__error">{errors.description}</i>
                        ) : null}
                        <div className="mobileOnly1">
                          <div className="spackInstruction">
                            <p>
                              <strong>Speak instead of typing</strong>
                            </p>
                            {/* <p>Press and speak</p> */}
                            {/* <p>We'll transcribe into the box above</p> */}
                          </div>
                          {/* <div className="spackInstruction">
                            <p>By tapping to talk, you agree to voice</p>
                            <p>
                              transcription and our <a href="/privacy-policy">Privacy Policy</a>
                            </p>
                          </div> */}
                          <div className="spackInstruction speakButton">
                            <button
                              type="button"
                              onClick={() => newFunctionForSpeech()} // Pass the current description value
                            >
                              {listening ? (
                                <img
                                  src="/images/mic_blue.png"
                                  width="24"
                                  height="24"
                                  className=""
                                  alt="microphone"
                                />
                              ) : (
                                <img
                                  src="/images/mic_cross_blue.png"
                                  className="blueColor"
                                  width="24"
                                  height="24"
                                  alt="microphone"
                                />
                              )}
                              <span>{listening ? 'Tap to stop' : 'Tap to talk'}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="cdf__form__final-step">
                <div className="cdf__field__wrapper">
                  <label>
                    <Field type="checkbox" name="terms" value="terms" />
                    <span>
                      I accept the{' '}
                      <a href="/privacy-policy" target="__blank">
                        terms & conditions
                      </a>
                      <span>*</span>
                    </span>
                  </label>
                  {errors.terms && touched.terms ? (
                    <i className="cdf__field__error">{errors.terms}</i>
                  ) : null}
                </div>
              </div>

              <p className="cdf__field__required">
                Required fields are marked <span>*</span>
              </p>

              <button
                disabled={!isValid || isSubmitting || formSubmittedSuccessfully}
                className="button"
                type="submit"
              >
                {isSubmitting ? (
                  <div className="submit-loader">
                    <img src="/images/cycle.svg" width="auto" height="auto" alt="cycle" />
                  </div>
                ) : (
                  'SUBMIT'
                )}
              </button>

              <p className="cdf__field__footer-text">Completely confidential</p>

              {error &&
                (error.kind === 'duplicate-enquiry' ? (
                  <p className="cdf__field__footer-text">
                    <strong>{error.message}</strong>
                  </p>
                ) : (
                  <>
                    <p className="cdf__field__footer-error">
                      <strong>There was an error when submitting your form</strong>, it is on us not
                      on you.
                    </p>
                    <p className="cdf__field__footer-error">
                      Please try again later or reach us directly at
                      communications@monacosolicitors.co.uk
                    </p>
                  </>
                ))}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

CDF.propTypes = {
  contactFieldLayout: PropTypes.bool,
};

export default CDF;
